import React, { FunctionComponent, memo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import type { LocationId } from 'Consts/types';
import { ROUTES } from 'Consts/routes';

import { IconNames } from 'UI/Elements/Icon';

import * as actions from 'State/actions';
import * as selectors from 'State/selectors';
import { AppDispatch } from 'State/store';
import useLocations from 'State/hooks/useLocations';

import MobileNavPanelUI from './Components/Mobile';
import DesktopNavPanelUI from './Components/Desktop';

export const getIconFromRoute = (route: string, selected?: boolean) => {
  const map = {
    [ROUTES.locations]: IconNames.MapPin,
    [ROUTES.home.index]: IconNames.Home,
    [ROUTES.ssidConfiguration.configuration]: IconNames.Home,
    [ROUTES.zones.secure.index]: IconNames.Layers,
    [ROUTES.zones.employee.index]: IconNames.Layers,
    [ROUTES.zones.guest.index]: IconNames.Layers,
    [ROUTES.shield]: IconNames.Topology,
    [ROUTES.network]: IconNames.Topology,
    [ROUTES.settings.secure]: IconNames.Settings,
    [ROUTES.ssidConfiguration.pods]: IconNames.Settings,
    [ROUTES.ssidConfiguration.support]: IconNames.Settings,
    [ROUTES.ssidConfiguration.account]: IconNames.Settings,
    [ROUTES.insurance.index]: IconNames.Guard,
    [ROUTES.devices]: IconNames.Devices,
    [ROUTES.employees]: IconNames.PersonProfile,
  };

  const selectedMap = {
    [ROUTES.locations]: IconNames.MapPin,
    [ROUTES.home.index]: IconNames.HomeFill,
    [ROUTES.ssidConfiguration.configuration]: IconNames.HomeFill,
    [ROUTES.zones.secure.index]: IconNames.Layers,
    [ROUTES.zones.employee.index]: IconNames.Layers,
    [ROUTES.zones.guest.index]: IconNames.Layers,
    [ROUTES.shield]: IconNames.Topology,
    [ROUTES.network]: IconNames.Topology,
    [ROUTES.settings.secure]: IconNames.SettingsFill,
    [ROUTES.ssidConfiguration.pods]: IconNames.Settings,
    [ROUTES.ssidConfiguration.support]: IconNames.Settings,
    [ROUTES.ssidConfiguration.account]: IconNames.Settings,
    [ROUTES.insurance.index]: IconNames.Guard,
    [ROUTES.devices]: IconNames.DevicesFill,
    [ROUTES.employees]: IconNames.PersonProfile,
  };

  return selected ? selectedMap[route] : map[route];
};

export type NavPanelItemProps = {
  currentRoute: string;
  route: string;
  additionalRoutesForSelection?: string[];
  onNavigate: (route: string) => void;
};

export type Props = {
  className?: string;
  currentRoute: string;
  onNavigate: (route: string) => void;
  showMultiLocationUI?: boolean;
};

type MobileNavPanelProps = {
  onNavigate: () => void;
};

export const MobileNavPanel: FunctionComponent<MobileNavPanelProps> = ({
  onNavigate,
}) => {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const ownedLocations = useSelector(selectors.locations.ownedLocations);

  const currentRoute = routerLocation.pathname;
  const showMultiLocationUI = (ownedLocations.data?.length || 0) > 1;

  const handleNavigation = useCallback(
    (route: string) => {
      navigate(route);

      if (onNavigate) {
        onNavigate();
      }
    },
    [navigate, onNavigate]
  );

  return (
    <MobileNavPanelUI
      currentRoute={currentRoute}
      onNavigate={handleNavigation}
      showMultiLocationUI={showMultiLocationUI}
    />
  );
};

export const DesktopNavPanel = memo(
  ({
    className,
    onNavigate,
  }: {
    className?: string;
    onNavigate: () => void;
  }) => {
    const dispatch = useDispatch<AppDispatch>();
    const routerLocation = useLocation();
    const navigate = useNavigate();

    const { activeLocation } = useLocations();

    const locationsForMenu = useSelector(selectors.locations.mergedLocations);
    const ownedLocations = useSelector(selectors.locations.ownedLocations);
    const navPanelHidden = useSelector(selectors.ui.page.navPanelHidden);
    const showMultiLocationUI = (ownedLocations.data?.length || 0) > 1;

    let currentRoute = routerLocation.pathname;

    const handleNavigation = useCallback(
      (route: string) => {
        navigate(route);

        if (onNavigate) {
          onNavigate();
        }
      },
      [navigate, onNavigate]
    );

    const handleSwitchToLocation = useCallback(
      (locationId: LocationId) => {
        navigate(ROUTES.home.index);
        currentRoute = '/';
        dispatch(actions.locations.switchLocation({ locationId }));
      },
      [dispatch]
    );

    return (
      <DesktopNavPanelUI
        isOpen={!navPanelHidden}
        className={className}
        currentRoute={currentRoute}
        onNavigate={handleNavigation}
        currentLocation={activeLocation.data}
        locations={locationsForMenu}
        onSwitchToLocation={handleSwitchToLocation}
        showMultiLocationUI={showMultiLocationUI}
      />
    );
  }
);
